import React from 'react';
import { useFox } from '../context/FoxContext';
import { X, ArrowRight, Zap, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const PlayfulFox = () => {
  const { foxState, hideFox } = useFox();
  const { t } = useLanguage(); // Dil fonksiyonunu al
  const navigate = useNavigate();

  if (!foxState.isVisible) return null;

  const handleClick = () => {
    if (foxState.action) {
        if (foxState.action === 'open_chat') {
            const event = new CustomEvent('openFoxChat', { detail: { message: t(foxState.messageKey) || foxState.messageKey } });
            window.dispatchEvent(event);
        } else {
            navigate(foxState.action);
        }
    }
    hideFox();
  };

  // Image Logic with Elite Fallbacks
  let imageSrc = '/fox-mascot.png'; 
  if (foxState.imageOverride) {
      imageSrc = foxState.imageOverride;
  } else {
      const posMap = {
          'walking': '/yuruyen.png',
          'peeking-top': '/Asagi.png',
          'bottom-left': '/solisaret.png',
          'bottom-right': '/sagMercek.png',
          'premium_mode': '/sagisaret.png'
      };
      imageSrc = posMap[foxState.position] || imageSrc;
  }

  // --- PREMIUM MODE UI ---
  if (foxState.position === 'premium_mode') {
    return (
      <div className="fixed bottom-0 right-0 z-[100] flex flex-row-reverse items-end pointer-events-none p-4 md:p-6 animate-in slide-in-from-right-32 duration-1000">
          <div className="relative pointer-events-auto group">
            <img 
              src={imageSrc} 
              alt="Elite Fox" 
              className="w-32 md:w-64 object-contain drop-shadow-[0_20px_50px_rgba(230,81,0,0.3)] cursor-pointer hover:scale-110 transition-all duration-500 origin-bottom-right"
              onClick={handleClick}
            />
            {/* Visual Aura for Premium */}
            <div className="absolute inset-0 bg-orange-500/5 blur-3xl -z-10 animate-pulse" />
          </div>

          <div 
            className="mb-24 md:mb-36 -mr-4 md:-mr-8 bg-white/95 dark:bg-[#111827]/95 backdrop-blur-xl p-4 md:p-6 rounded-[2rem] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] border border-slate-100 dark:border-white/10 relative max-w-[180px] md:max-w-[240px] pointer-events-auto cursor-pointer ring-1 ring-black/5 animate-in zoom-in duration-500 delay-500"
            onClick={handleClick}
          >
            <div className="flex flex-col gap-3">
              <p className="text-[11px] md:text-sm font-black text-slate-800 dark:text-white leading-tight italic tracking-tight">
                {t(foxState.messageKey) || foxState.messageKey}
              </p>
              <div className="flex items-center gap-2">
                <span className="bg-orange-500/10 text-orange-600 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 animate-bounce">
                  {t('fox_take_action') || 'Take Action'} <ArrowRight size={10} strokeWidth={3} />
                </span>
              </div>
            </div>
            <button 
              onClick={(e) => { e.stopPropagation(); hideFox(); }} 
              className="absolute -top-2 -left-2 w-6 h-6 bg-white dark:bg-slate-800 rounded-full shadow-lg border border-slate-100 dark:border-white/10 flex items-center justify-center text-slate-400 hover:text-rose-500 transition-colors"
            >
              <X size={12} />
            </button>
            {/* Speech Tail */}
            <div className="absolute bottom-6 -right-2 w-4 h-4 bg-white dark:bg-[#111827] border-r border-b border-slate-100 dark:border-white/10 rotate-[-45deg]" />
          </div>
      </div>
    );
  }

  // --- WALKING MODE UI ---
  if (foxState.position === 'walking') {
    return (
      <div className="fox-patrol-container">
         <div 
            className="absolute -top-28 md:-top-32 left-1/2 -translate-x-1/2 bg-white/95 dark:bg-[#111827]/95 backdrop-blur-xl p-4 rounded-3xl shadow-2xl border border-orange-500/20 w-44 md:w-56 cursor-pointer pointer-events-auto z-50 animate-in fade-in zoom-in duration-500"
            onClick={handleClick}
         >
            <p className="text-[10px] md:text-xs font-black text-slate-700 dark:text-slate-200 text-center leading-tight">
                {t(foxState.messageKey) || foxState.messageKey}
            </p>
            <button 
              onClick={(e) => { e.stopPropagation(); hideFox(); }} 
              className="absolute -top-2 -right-2 w-5 h-5 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center text-slate-400 hover:text-rose-500"
            >
              <X size={10} />
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 bg-white dark:bg-[#111827] border-b border-r border-orange-500/20 rotate-45" />
         </div>
         <img src={imageSrc} alt="Fox" className="fox-walking-body w-24 md:w-full drop-shadow-xl" onClick={handleClick} />
      </div>
    );
  }

  // --- CORNER MODES (Bottom Left/Right/Peeking) ---
  const isLeft = foxState.position === 'bottom-left';
  const isPeeking = foxState.position === 'peeking-top';

  return (
    <div 
      className={`fixed z-[100] flex items-end gap-3 pointer-events-none 
        ${isPeeking ? 'top-0 left-1/2 -translate-x-1/2 flex-col pt-4' : 
          isLeft ? 'bottom-0 left-0 md:left-6 flex-row' : 'bottom-0 right-0 md:right-6 flex-row-reverse'}`}
    >
      <div className={`relative pointer-events-auto animate-in ${isPeeking ? 'slide-in-from-top-32' : 'slide-up-bounce'} duration-700`}>
        <img 
          src={imageSrc} 
          alt="Fox Assist" 
          className={`${isPeeking ? 'w-24 md:w-48' : 'w-32 md:w-56'} object-contain drop-shadow-2xl cursor-pointer hover:scale-110 transition-transform duration-500`}
          onClick={handleClick}
        />
        {/* Active Action Glow */}
        {foxState.action && <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-orange-500/10 rounded-full blur-2xl animate-pulse" />}
      </div>

      <div 
        className={`bg-white/95 dark:bg-[#111827]/95 backdrop-blur-xl p-4 md:p-5 rounded-[2rem] shadow-2xl border border-slate-100 dark:border-white/10 relative max-w-[180px] md:max-w-[220px] pointer-events-auto cursor-pointer animate-in fade-in zoom-in duration-500 delay-300 
          ${isPeeking ? 'mt-4' : 'mb-20 md:mb-28'} 
          ${isLeft ? 'origin-bottom-left' : isPeeking ? 'origin-top' : 'origin-bottom-right'}`}
        onClick={handleClick}
      >
        <div className="flex flex-col gap-2">
            <p className="text-[11px] md:text-sm font-bold text-slate-800 dark:text-slate-200 text-center leading-relaxed italic">
                {t(foxState.messageKey) || foxState.messageKey}
            </p>
            {foxState.action && (
                <div className="flex justify-center">
                    <span className="text-[8px] font-black uppercase tracking-[0.2em] text-orange-600 bg-orange-50 dark:bg-orange-500/10 px-3 py-1 rounded-full flex items-center gap-1.5">
                        {t('fox_examine') || 'Examine'} <ArrowRight size={10} strokeWidth={3} />
                    </span>
                </div>
            )}
        </div>
        
        <button 
          onClick={(e) => { e.stopPropagation(); hideFox(); }} 
          className="absolute -top-2 -right-2 w-5 h-5 bg-white dark:bg-slate-800 rounded-full shadow-md border border-slate-100 dark:border-white/10 flex items-center justify-center text-slate-400 hover:text-rose-500"
        >
          <X size={10} />
        </button>

        {/* Dynamic Tail Position */}
        {!isPeeking ? (
            <div className={`absolute bottom-6 w-3 h-3 bg-white dark:bg-[#111827] border-b border-slate-100 dark:border-white/10 rotate-45 
              ${isLeft ? '-left-1.5 border-l border-b-0 border-r-0' : '-right-1.5 border-r border-b border-t-0 border-l-0'}`} 
            />
        ) : (
            <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white dark:bg-[#111827] border-t border-l border-slate-100 dark:border-white/10 rotate-45" />
        )}
      </div>
    </div>
  );
};

export default PlayfulFox;